import { getAdditionalUserInfo } from 'firebase/auth';
import { message } from '@linktivity/link-ui';
import { Firebase, type UserCredential } from '@linktivity/link-utils';
export type { User, UserCredential } from '@linktivity/link-utils';
import i18n from '@link/share/i18n';
import { BaseAuth } from '@link/share/stores';
import { endGa4AccountFlowOnLogout, takeGa4LogoutLanding } from './ga4/flow';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_APP_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_APP_FIREBASE_AUTH_DOMAIN,
  tenantId: import.meta.env.VITE_APP_FIREBASE_TENANT_ID
};

const tenantId = import.meta.env.VITE_APP_FIREBASE_TENANT_ID;

class ShareAuth extends Firebase {
  private authStore: BaseAuth;

  constructor(auth: BaseAuth) {
    super(firebaseConfig);
    this.setTenantId(tenantId);
    this.authStore = auth;

    this.auth.onAuthStateChanged(user => {
      if (user) {
        auth.setUser(user);
      } else {
        auth.clearUser();
      }
      auth.setAuthReady();
    });
  }

  // Revoke ARS tokens first: firebase sign-out clears them via onAuthStateChanged.
  // The GA4 flow ends before that, since a private route redirects to login as
  // soon as the tokens are gone.
  async logout() {
    endGa4AccountFlowOnLogout();
    try {
      await this.authStore.revokeToken();
      return await super.logout();
    } catch (error) {
      // Still signed in, so no login view follows: drop the mark, or it would
      // swallow the next real one.
      takeGa4LogoutLanding();
      throw error;
    }
  }

  showErrorMessage(error: unknown) {
    // Type guard for Firebase error objects
    if (
      typeof error === 'object' &&
      error !== null &&
      'code' in error &&
      'message' in error &&
      typeof (error as { code: unknown }).code === 'string' &&
      typeof (error as { message: unknown }).message === 'string'
    ) {
      const errorCode = (error as { code: string }).code;
      let errorMessage = (error as { message: string }).message;
      if (i18n.exists(`share.firebase.${errorCode}`)) {
        errorMessage = i18n.t(`share.firebase.${errorCode}`);
      }
      message.error(errorMessage);
    } else {
      message.error(i18n.t('share.firebase.unknownError'));
    }
  }
}

/**
 * Whether this sign-in created the Firebase account. Undefined when Firebase
 * does not say, so callers never guess.
 */
export function isNewFirebaseUser(
  credential: UserCredential
): boolean | undefined {
  return getAdditionalUserInfo(credential)?.isNewUser;
}

export default ShareAuth;
